import { computed, inject } from '@angular/core';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { patchState } from '@app/patch-state';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withDevtools } from '@app/devtools';
import { of, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { AnalysisCustomWorkflowStatus, CaseHistoryRecord } from '../models';
import { AnalysisService } from '../modules/analyses/services/analysis.service';
import {
  analysisStatusDefaultUserOptions,
  AnalysisWorkflowConfig,
  AnalysisWorkflowStatus,
} from '../modules/analyses/store/models';
import { WorkbenchService } from '../modules/workbench/store/services/workbench.service';

export interface AnalysisModuleStoreState {
  caseHistoryData: CaseHistoryRecord[];
  caseHistoryLoading: boolean;
  caseHistoryLoaded: boolean;

  customStatusData: AnalysisCustomWorkflowStatus[];
  customStatusLoading: boolean;
  customStatusLoaded: boolean;
}

const initialState: AnalysisModuleStoreState = {
  caseHistoryData: [],
  caseHistoryLoading: false,
  caseHistoryLoaded: false,

  customStatusData: [],
  customStatusLoading: false,
  customStatusLoaded: false,
};

export const AnalysisModuleStore = signalStore(
  { providedIn: 'root' },
  withDevtools('AnalysisModule'),
  withState(initialState),
  withComputed((store) => ({
    customOrDefaultAnalysisStatusOptions: computed(() => {
      const options = store.customStatusData();
      return options?.length
        ? options
        : analysisStatusDefaultUserOptions.map((option) => ({
            status: option,
            status_description: AnalysisWorkflowConfig[option].label,
            parent_status: option,
            parent_status_description: AnalysisWorkflowConfig[option].label,
          }));
    }),
  })),
  withMethods((store) => {
    const analysisService = inject(AnalysisService);
    const workbenchService = inject(WorkbenchService);

    const loadCaseHistory = rxMethod<{ analysisId: number }>(
      pipe(
        tap(() => patchState(store, { caseHistoryData: [], caseHistoryLoading: true, caseHistoryLoaded: false })),
        switchMap(({ analysisId }) =>
          workbenchService.getCaseHistory(analysisId).pipe(
            tap((data) =>
              patchState(store, { caseHistoryData: data, caseHistoryLoading: false, caseHistoryLoaded: true }),
            ),
            catchError(() => {
              patchState(store, { caseHistoryLoading: false, caseHistoryLoaded: false });
              return of(null);
            }),
          ),
        ),
      ),
    );

    const loadCustomStatusOptions = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { customStatusData: [], customStatusLoading: true, customStatusLoaded: false })),
        switchMap(() =>
          analysisService.getCustomStatusOptions().pipe(
            tap((data) =>
              patchState(store, { customStatusData: data, customStatusLoading: false, customStatusLoaded: true }),
            ),
            catchError(() => {
              patchState(store, { customStatusLoading: false, customStatusLoaded: true });
              return of(null);
            }),
          ),
        ),
      ),
    );

    return {
      loadCaseHistory,
      loadCustomStatusOptions,

      getAnalysisStatusOptions(analysis: {
        status: number;
        parent_status: AnalysisWorkflowStatus;
        status_description: string;
      }): AnalysisCustomWorkflowStatus[] {
        const options = store.customOrDefaultAnalysisStatusOptions();
        return options.some((o) => o.status === analysis.status)
          ? [...options]
          : [
              ...options,
              {
                status: analysis.status,
                parent_status: analysis.parent_status,
                status_description: analysis.status_description,
              },
            ];
      },
    };
  }),
);
